import { forwardRef, useState, type InputHTMLAttributes, type CSSProperties, type FocusEvent } from "react";
import { useEmbedTokens, useReducedMotion, motionSafe } from "../theme";

interface Props extends Omit<InputHTMLAttributes<HTMLInputElement>, "style"> { invalid?: boolean; }

// TD-319: ref-forwarding is required, not cosmetic — a consumer that needs
// imperative focus (autofocus code inputs) cannot use this primitive without it
// and has to fall back to a raw <input>, which is what left raw elements in the
// embed in the first place.
export const EmbedInput = forwardRef<HTMLInputElement, Props>(function EmbedInput(
  { invalid = false, disabled, onFocus, onBlur, ...rest }: Props,
  ref,
) {
  const t = useEmbedTokens();
  const reduced = useReducedMotion();
  const [focused, setFocused] = useState(false);
  const borderColor = invalid ? t.colors.error : focused ? t.colors.borderFocus : t.colors.border;
  const style: CSSProperties = {
    width: "100%", fontFamily: "inherit", fontSize: t.inputFontSize, color: t.colors.text,
    height: t.buttonHeight, padding: `0 ${t.inputPadding}px`, borderRadius: t.borderRadius,
    background: disabled ? t.colors.disabledBg : t.colors.inputBg, border: `1px solid ${borderColor}`,
    outline: "none", boxShadow: focused && !invalid ? `0 0 0 3px ${t.colors.primaryTint}` : "none",
    transition: motionSafe(reduced, "border-color .12s, box-shadow .12s"), boxSizing: "border-box",
  };
  return (
    <input {...rest} ref={ref} disabled={disabled} style={style}
      onFocus={(e: FocusEvent<HTMLInputElement>) => { setFocused(true); onFocus?.(e); }}
      onBlur={(e: FocusEvent<HTMLInputElement>) => { setFocused(false); onBlur?.(e); }} />
  );
});
